import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GetAllOrder } from "../../../Services/OrderService";

const TrackingPage = () => {
    const [trackingId, setTrackingId] = useState("");
    const [steps, setSteps] = useState([]);
    const [error, setError] = useState("");
    const [isTracking, setIsTracking] = useState(false);
    const [dates, setDates] = useState({ created: null, delivered: null });
    const [paymentStatus, setPaymentStatus] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("");
    const [loadingState, setLoadingState] = useState({ makePayment: null });
    const [order, setOrder] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();
    const orderGuidFromState = location.state?.orderGuid;

    const handleMakePayment = () => {
        if (loadingState.makePayment) return;
        setLoadingState((prev) => ({ ...prev, makePayment: order?.orderId }));

        navigate(`/order-payment`, { state: { order: order } });

        setTimeout(() => {
            setLoadingState((prev) => ({ ...prev, makePayment: null }));
        }, 500);
    };

    const handleTrack = async (guidToTrack = "") => {
        setError("");
        setSteps([]);
        setDates({ created: null, delivered: null });
        setPaymentStatus("");
        setPaymentMethod("");
        setIsTracking(true);

        const currentGuid = guidToTrack != "" ? guidToTrack : trackingId.trim();

        if (!currentGuid) {
            setError("Please enter a tracking ID.");
            setIsTracking(false);
            return;
        }

        try {
            const userId = localStorage.getItem("userId");
            if (!userId) {
                setError("User not logged in.");
                setIsTracking(false);
                return;
            }

            const response = await GetAllOrder({ userId: parseInt(userId), orderGuid: currentGuid });

            if (response.status && response.data?.orders?.length > 0) {
                const order = response.data.orders[0];

                setOrder(order);

                setPaymentStatus(order.paymentStatus); // <-- Save payment status
                setPaymentMethod(order.paymentMethod); // <-- Save payment method

                const stepsList = getSteps(order);
                setSteps(stepsList);

                setDates({
                    created: order.createdOnUtc,
                    delivered: order.deliveryDate
                });
            } else {
                setError(`Tracking ID ${trackingId} not found.`);
            }
        } catch (err) {
            console.error("Error fetching order:", err);
            setError("An error occurred while fetching the order.");
        } finally {
            setIsTracking(false);
        }
    };

    useEffect(() => {
        if (orderGuidFromState) {
            setTrackingId(orderGuidFromState);
            handleTrack(orderGuidFromState);
        }
    }, [orderGuidFromState]);

    const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    };

    // ---------------------------------------------------
    //  MAIN LOGIC: Payment Status Based Steps
    // ---------------------------------------------------
    const getSteps = (order) => {
        const steps = [];

        // 🔴 If Order CANCELED → show Order Placed and Canceled
        if (order.orderStatus === "Cancelled") {
            steps.push({
                status: "Order Placed",
                time: formatDate(order.createdOnUtc),
            });

            steps.push({
                status: "Cancelled",
                time: "",
            });

            return steps;
        }

        // 🟡 If Payment NOT DONE and NOT COD → show very limited steps
        if (order.paymentStatus !== "Paid" && order.paymentMethod !== "COD") {
            steps.push({
                status: "Order Placed",
                time: formatDate(order.createdOnUtc),
            });

            steps.push({
                status: "Awaiting Payment",
                time: "",
            });

            return steps;
        }

        // 🟢 PAYMENT PAID or COD — show full professional tracking
        steps.push({
            status: "Order Placed",
            time: formatDate(order.createdOnUtc),
        });

        if (["Processing", "OutForDelivery", "Completed"].includes(order.orderStatus)) {
            steps.push({ status: "Processing", time: "" });
        }

        if (["OutForDelivery", "Completed"].includes(order.orderStatus)) {
            steps.push({ status: "Out for Delivery", time: "" });
        }

        if (order.orderStatus === "Completed") {
            steps.push({
                status: "Delivered",
                time: formatDate(order.deliveryDate),
            });
        }

        return steps;
    };

    const currentStatus = steps.length > 0 ? steps[steps.length - 1].status : null;
    const isDelivered = currentStatus === "Delivered";

    return (
        <div className="tracking-page pt_74 pb_74">
            <div className="container">
                <h2 className="text-center mb-4">📦 Order Tracking</h2>

                {/* Input Section */}
                <div style={{ maxWidth: "800px", margin: "0 auto" }} className="mb-4">
                    <div className="input-group mb-4">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Order ID or Order GUID"
                            value={trackingId}
                            onChange={(e) => setTrackingId(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleTrack();
                            }}
                        />
                        <button
                            className="btn btn-primary"
                            onClick={() => handleTrack()}
                            disabled={isTracking}
                        >
                            {isTracking ? "Tracking..." : "Track Order"}
                        </button>
                    </div>
                </div>

                {/* Errors */}
                {error && <p className="alert alert-danger text-center">{error}</p>}

                {/* Results */}
                {steps.length > 0 && (
                    <div className="tracking-results">

                        {/* Status Banner */}
                        <div
                            className={`status-banner ${paymentStatus !== "Paid" && paymentMethod !== "COD"
                                ? "payment-pending"
                                : isDelivered
                                    ? "delivered"
                                    : "in-progress"
                                }`}
                        >
                            {paymentStatus !== "Paid" && paymentMethod !== "COD" ? (
                                <>
                                    <h3>🟡 Payment Pending</h3>
                                    <p>Please complete the payment to begin processing.</p>
                                </>
                            ) : paymentMethod === "COD" && paymentStatus !== "Paid" ? (
                                <>
                                    <h3>💰 Payment on Delivery</h3>
                                    <p>Order is being processed. Payment will be collected upon delivery.</p>
                                </>
                            ) : isDelivered ? (
                                <h3>🎉 Order Delivered!</h3>
                            ) : (
                                <h3>Current Status: {currentStatus}</h3>
                            )}

                            <p>Tracking ID: <b>{trackingId.toUpperCase()}</b></p>
                            <p>Payment Status: <b>{paymentStatus}</b></p>
                            <p>Payment Method: <b>{paymentMethod}</b></p>
                        </div>

                        {/* Tracking Steps */}
                        <div className="tracking-steps-container">
                            {steps.map((item, index) => {
                                const isCurrent = index === steps.length - 1;
                                const isCompleted = index < steps.length - 1 || isCurrent;

                                return (
                                    <div
                                        key={index}
                                        className={`tracking-step ${isCompleted ? "is-completed" : ""} ${isCurrent ? "is-current" : ""}`}
                                    >
                                        <div className="timeline-line"></div>
                                        <div className="circle">
                                            {isCompleted && <i className="icon-check">✓</i>}
                                        </div>

                                        <div className="step-content">
                                            <span className="status-name">{item.status}</span>
                                            <span className="timestamp">{item.time}</span>

                                            {/* SHOW BUTTON INSIDE THIS STEP ONLY FOR NON-COD ORDERS */}
                                            {item.status === "Awaiting Payment" && paymentStatus !== "Paid" && paymentMethod !== "COD" && (
                                                <button
                                                    className="btn btn-primary mt-2"
                                                    onClick={() => handleMakePayment()}
                                                    disabled={loadingState.makePayment === order.orderId}
                                                    style={{
                                                        background: "#A37C4D",
                                                        border: "none",
                                                        padding: "8px 16px",
                                                        borderRadius: "6px",
                                                        fontWeight: "600",
                                                        fontSize: "14px"
                                                    }}
                                                >
                                                    {loadingState.makePayment === order.orderId
                                                        ? "Redirecting..."
                                                        : "Make Payment"}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TrackingPage;
